/**
 * Plain-language Stripe Identity status for the UI (Oct 10 2026).
 * Server buckets Stripe's last_error.code into a reason (see
 * server/api-util/stripeIdentity.js identityErrorReason). Same words as the
 * result email (server/api-util/identityResultEmail.js).
 */

export const IDENTITY_REASON_TEXT = {
  doc_type:
    "We couldn't use that document. Please use a driver's license, state ID or passport.",
  expired: 'That ID looks expired. Please use a current, unexpired ID.',
  unreadable:
    "We couldn't read your ID photo, so try again in good light, with the whole card in the frame and no glare.",
  selfie:
    "The selfie didn't match the photo on your ID. Try again facing the camera in good light, with nothing covering your face.",
  abandoned:
    "It looks like the ID check was closed before it finished. It only takes about two minutes.",
  consent: 'Consent was declined, so the ID check stopped. Tap Accept when Stripe asks, then continue.',
  device: "This device isn't supported, so try on your phone.",
  underage: 'You must be 18 or older to use the site.',
  generic: "Stripe couldn't finish checking your ID. Please try again.",
  manual_rejected:
    "We couldn't approve the photo you sent. Please try the ID check again, or email info@sweetlisteners.com for help.",
};

export const MANUAL_UPLOAD_PROMPT =
  'Add 2 photos: one of your ID, and one selfie of you holding that ID next to your face. A person on our team will check them.';
// Oct 17 2026: smaller "Still stuck?" option under Try again, with an example and checklist.
export const MANUAL_UPLOAD_TOGGLE = 'Still stuck? Send us a photo instead';
export const MANUAL_UPLOAD_TITLE = 'Send 2 photos for a person to check';
export const MANUAL_UPLOAD_EXAMPLE_CAPTION = 'Selfie like this: your face and your whole ID card, side by side.';
export const MANUAL_UPLOAD_CHECKLIST = [
  'Photo 1: the front of your ID, all 4 corners showing',
  'Photo 2: you holding the same ID next to your face',
  'Good light, no glare, not blurry',
];
export const MANUAL_UPLOAD_NOTE =
  "A computer first checks that your face and ID are both showing in the selfie. Then only our team looks at the photos, and they're deleted after review. They're never shown on your profile.";
// What we tell someone whose photo Ops rejected (key = reason Ops picked).
export const MANUAL_REJECT_TELL = {
  no_id: "We couldn't see an ID card in your photo.",
  face: "We couldn't see your whole face in your photo.",
  blurry: 'Your photo was too blurry or dark to read.',
  id_cut_off: "Part of your ID was cut off or couldn't be read.",
  mismatch: "We couldn't match your photo to the ID or to your account.",
  other: "We couldn't approve your photo.",
};
export const manualRejectText = reason =>
  `${MANUAL_REJECT_TELL[reason] || MANUAL_REJECT_TELL.other} Please send a new photo using the tips below, or try the ID check again.`;
export const MANUAL_PENDING_TITLE = 'Your photo is being reviewed';
export const MANUAL_PENDING_BODY =
  "Thanks. We got your photo and a person will review it soon. We'll email you when it's approved. Your photo is kept private and deleted after review.";

export const IDENTITY_TIPS =
  'Tips: use the real card, not a photo of it. If you use a screen reader, a sighted helper can hold the phone for the ID photo.';

export const NEEDS_RETRY_TITLE = "Your ID check didn't finish. Try again";
export const RETRY_BUTTON = 'Try again';

export const identityReasonText = reason =>
  IDENTITY_REASON_TEXT[reason] || IDENTITY_REASON_TEXT.generic;

/**
 * @param {Object} user currentUser
 * @returns {{ status: string|null, reason: string|null, needsRetry: boolean }}
 */
export const identityCheckStateFromUser = user => {
  const pd = user?.attributes?.profile?.protectedData || {};
  const status = typeof pd.stripeIdentityStatus === 'string' ? pd.stripeIdentityStatus : null;
  const reason =
    typeof pd.stripeIdentityErrorReason === 'string' ? pd.stripeIdentityErrorReason : null;
  const needsRetry = status === 'requires_input' || status === 'canceled';
  return {
    status,
    reason: needsRetry ? (status === 'canceled' ? 'abandoned' : reason || 'generic') : null,
    needsRetry,
  };
};

/**
 * Prefer the server's fresh re-check (onboarding status .identityRetry) over the
 * possibly stale currentUser copy (Oct 17 2026).
 */
export const identityCheckState = (user, setupStatus) => {
  const fromUser = identityCheckStateFromUser(user);
  if (setupStatus && setupStatus.done && setupStatus.done.identity === true) {
    return { ...fromUser, needsRetry: false, reason: null };
  }
  const r = setupStatus && setupStatus.identityRetry;
  if (r && r.status) {
    return { status: r.status, reason: r.reason || 'generic', needsRetry: true };
  }
  if (setupStatus && Array.isArray(setupStatus.steps) && fromUser.needsRetry === false) {
    return fromUser;
  }
  return fromUser;
};
